import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, withStyles, useMediaQuery, CircularProgress } from '@material-ui/core';

import { format } from 'date-fns';

import { changeModalVisibility } from '../../../redux/actions/auth';
import { fetchTime, setCurrentConfirmStep } from '../../../redux/actions/book-appointment';
import { chooseTime, changeStaff, changeStep, updateAppointments } from '../../../redux/actions/book-appointment';
import { useServicesDuration } from '../../../utils/hooks/bookAppointment';
import {
    getCurrentAppointments,
    getCurrentDate,
    getCurrentTime,
    getTimes,
    getCurrentLocation,
    getOrganisation
    // getCourseContext
} from '../../../redux/selectors/book-appointment';

import Timeout from './Timeout';

import { CONFIRM } from '../../../constants/steps';
import { time as timeStyle } from './styles';

// import { TIME_IO_FROM_FRONT } from '../../../constants/socket';
import { MOBILE } from '../../../constants/breakpoints';

import { fetchPractitioners } from '../../../api/practitionerApi';
import { getCurrentTimezonedDate } from '../../../collums-components/helpers/timezone';
import { getLocationItem } from '../../../collums-constants/utils';
import { addMinutes } from 'date-fns/esm';

let interval;

export const MINIMISE_GAPS_STRATEGY = {
    ALWAYS_MINIMISE: 'Always minimise gaps',
    SHOW_ALL_SLOTS: 'Minimise after first booking'
};

export const BOUNDARY_SLOT_TYPES = {
    FIRST_OF_THE_DAY: 'First of the day',
    LAST_OF_THE_DAY: 'Last of the day'
};

const TimeChoose = ({ socket, classes }) => {
    const dispatch = useDispatch();

    const [displayFade, setDisplayFade] = useState({
        top: false,
        bottom: true
    });

    const times = useSelector(getTimes) || [];
    const org = useSelector(getOrganisation);
    const user = useSelector(state => state.auth.user);
    const isReschedule = useSelector(state => state.bookAppointment.isReschedule);
    const currentDate = useSelector(getCurrentDate);
    const currentTime = useSelector(getCurrentTime);
    const currentAppt = useSelector(getCurrentAppointments);
    const currentLocation = useSelector(getCurrentLocation);
    // const courseContext = useSelector(getCourseContext);

    const minimiseGaps = org?.minimiseGaps;
    const minimiseGapsStrategy = org?.minimiseGapsStrategy;
    const alsoShowFirst = org?.alsoShowFirst;
    const alsoShowLast = org?.alsoShowLast;

    const showAllApptsFirst = minimiseGapsStrategy === MINIMISE_GAPS_STRATEGY.SHOW_ALL_SLOTS;

    const minimalGapTimes = timesArr => {
        const length = timesArr.length;
        if (!length) return [];

        const firstAdjacentTimes = timesArr.filter(t => t.apptAdjacent === true);

        let gapAdjacentTimes = [];
        const duration = Number(timesArr.at(0).duration);

        for (const [idx, time] of timesArr.entries()) {
            if (idx < length - 1) {
                const t1 = new Date(timesArr[idx + 1].value);
                const t2 = new Date(time.value);
                const minDiff = Math.floor(Math.abs(t2 - t1) / 1000 / 60);
                if (minDiff > duration) gapAdjacentTimes.push(time, timesArr[idx + 1]);
            }
        }
        return [...new Set([...gapAdjacentTimes, ...firstAdjacentTimes])];
    };

    const additionalTimes = (showFirst, showLast, timesArr) => {
        if (!minimiseGaps || (!showFirst && !showLast) || !timesArr || !timesArr.length) return [];

        let additionalTimes = [];
        if (showFirst) {
            const firstOfDay = timesArr.find(t => t.isFirstOfDay);
            if (firstOfDay) additionalTimes.push(firstOfDay);
        }
        if (showLast) {
            const lastOfDay = [...timesArr].reverse().find(t => t.isLastOfDay);
            if (lastOfDay) additionalTimes.push(lastOfDay);
        }

        return additionalTimes;
    };

    const history = useHistory();
    const { url } = useRouteMatch();
    const location = useLocation();

    const scrollerRef = useRef();
    const servicesDuration = useServicesDuration();
    const isMobile = useMediaQuery(`(max-width:${MOBILE}px)`);

    const [staff, setStaff] = useState([]);
    const [countdown, setCountdown] = useState(300);
    const [selectedHour, setSelectedHour] = useState(null);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useLayoutEffect(() => {
        if (!scrollerRef.current) return;

        const { scrollTop, offsetHeight, scrollHeight } = scrollerRef.current;

        if (scrollTop > 0) {
            setDisplayFade({ ...displayFade, top: true });
        } else {
            setDisplayFade({
                top: false,
                bottom: true
            });
        }

        if (scrollTop + offsetHeight >= scrollHeight) {
            setDisplayFade({
                ...displayFade,
                bottom: false
            });
        }

        if (scrollTop > 0 && scrollTop + offsetHeight < scrollHeight) {
            setDisplayFade({
                top: true,
                bottom: true
            });
        }
        // eslint-disable-next-line
    }, [scrollerRef.current?.scrollTop]);

    const handleCountdown = useCallback(() => {
        interval = setInterval(() => {
            setCountdown(current => {
                if (current === 0) {
                    setSelectedHour(null);
                    dispatch(chooseTime(null));
                    clearInterval(interval);

                    // When time countdown ends, clear the selected time in back-end
                    // socket.send({
                    //     type: TIME_IO_FROM_FRONT,
                    //     payload: null
                    // });

                    return current;
                }
                return current - 1;
            });
        }, 1000);
        /*eslint-disable-next-line */
    }, [socket]);

    useEffect(() => {
        return () => {
            clearInterval(interval);
        };
    }, [socket]);

    useEffect(() => {
        if (isLoginOpen) {
            dispatch(changeStep(CONFIRM));
        }
    }, [user, dispatch, isLoginOpen]);

    useEffect(() => {
        if (!currentLocation.id) return;

        const apptsIds = currentAppt.map(appt => appt.appointmentId);
        const data = currentAppt.map((appt, index) => {
            return {
                ...(appt.staff && { practitioner: appt.staff.id }),
                appointmentBeingReschedule: [...apptsIds],
                date: currentDate,
                service: appt.service,
                clinic: currentLocation.id,
                user: user.id,
                withHolidays: true,
                forceInterval: index === 0 ? null : 5
            };
        });

        dispatch(fetchTime(data, setIsLoading, minimiseGaps, showAllApptsFirst));
    }, [
        dispatch,
        currentDate,
        currentLocation.id,
        servicesDuration,
        currentAppt,
        user.id,
        minimiseGaps,
        showAllApptsFirst
    ]);

    useEffect(() => {
        if (!currentLocation.id) return;
        if (!currentAppt || currentAppt.length === 0) return;

        const service = currentAppt[0]?.service;
        if (!service) return;

        const fetch = async () => {
            const payload = {
                month: getCurrentTimezonedDate().getMonth(),
                clinic: currentLocation.id,
                service
            };

            const data = await fetchPractitioners(payload);

            setStaff(data);
        };

        fetch();
    }, [currentLocation.id, dispatch, currentAppt]);

    const getSelectedStaff = (hour, index = 0) => {
        let practitionerId;

        if (index > 0 && hour.otherPractitioners[index - 1]) {
            practitionerId = hour.otherPractitioners[index - 1].id;
        } else {
            practitionerId = hour.practitioner.id;
        }

        let currentStaff = { ...staff.find(staff => staff.id === practitionerId) };

        if (
            index !== 0 &&
            currentStaff &&
            Array.isArray(hour.otherDurations) &&
            hour.otherDurations.length > index - 1
        ) {
            currentStaff.duration = hour.otherDurations[index - 1];
        }
        return currentStaff;
    };

    const formatDate = value => {
        const date = getCurrentTimezonedDate(value);
        const pattern = 'HH:mma';

        const formatedDate = format(date, pattern);
        return formatedDate;
    };

    const renderTimeOptions = () => {
        // if (currentAppt.length > 1 && times && times.length) {
        //     const apptToReschedule = moment(getCurrentTimezonedDate(currentAppt[0].event.start));
        //     const linkedAppts = currentAppt.slice(1, currentAppt.length);
        //     if (times.length < 1) {
        //         return false;
        //     }
        //     let timesToReschedule = times.filter(time => time[0].practitioner.id === currentAppt[0].staff.id)[0];
        //     linkedAppts.forEach(linkedAppts => {
        //         const apptToCompare = moment(getCurrentTimezonedDate(linkedAppts.event.start));
        //         const difference = moment.duration(apptToCompare.diff(apptToReschedule)).asMinutes();
        //         const linkedTimes = times.filter(time => time[0].practitioner.id === linkedAppts.staff.id)[0];
        //         const formatTimesLinkedAppt = linkedTimes.map(time =>
        //             moment(getCurrentTimezonedDate(time.value)).format('HH:mm')
        //         );
        //         timesToReschedule = timesToReschedule.filter(timeToReschedule => {
        //             const formatTimeToReschedule = moment(getCurrentTimezonedDate(timeToReschedule.value));
        //             return formatTimesLinkedAppt.includes(
        //                 formatTimeToReschedule.add(difference, 'minutes').format('HH:mm')
        //             );
        //         });
        //     });
        //     return ((timesToReschedule && timesToReschedule.length && timesToReschedule) || []).map((el, index) => {
        //         const date = getCurrentTimezonedDate(el.value);
        //         return (
        //             <div
        //                 key={index}
        //                 className={`${classes.hourOption} ${selectedHour &&
        //                     date.getTime() === selectedHour.value.getTime() &&
        //                     classes.selectedOption} ${!el.valid && classes.disabledOption}`}
        //                 onClick={() => {
        //                     if (el.valid) {
        //                         handleSelectedHour({
        //                             ...el,
        //                             value: date
        //                         });
        //                     }
        //                 }}
        //             >
        //                 {`${formatDate(el.value)}`}
        //             </div>
        //         );
        //     });
        // }

        let combinedTimes = (times && times.length && times[0] !== undefined && times[0].length && times[0]) || [];
        if (combinedTimes.length && times.find(t => t.length === 0)) {
            return [];
        }

        let otherServicesDuration = 0;

        for (let i = 1; i < times.length; i++) {
            const currentServicesTimes = times[i];
            // eslint-disable-next-line no-loop-func
            combinedTimes = combinedTimes.map(time => {
                if (!Array.isArray(time.otherDurations)) {
                    time.otherDurations = [];
                }
                time.otherDurations.push(currentServicesTimes[0].duration);
                if (!Array.isArray(time.otherPractitioners)) {
                    time.otherPractitioners = [];
                }
                return time;
            });
            combinedTimes = combinedTimes
                // eslint-disable-next-line no-loop-func
                .filter(time => {
                    const mainServiceDuration = Number(time.duration);
                    const nextAppointmentTime = addMinutes(
                        new Date(time.value),
                        mainServiceDuration + otherServicesDuration
                    );
                    return currentServicesTimes.find(
                        oaTime => new Date(oaTime.value).getTime() === nextAppointmentTime.getTime()
                    );
                })
                // eslint-disable-next-line no-loop-func
                .map(time => {
                    const mainServiceDuration = Number(time.duration);
                    const nextAppointmentTime = addMinutes(
                        new Date(time.value),
                        mainServiceDuration + otherServicesDuration
                    );
                    const matchingTime = currentServicesTimes.find(
                        oaTime => new Date(oaTime.value).getTime() === nextAppointmentTime.getTime()
                    );
                    if (matchingTime) {
                        time.otherPractitioners.push(matchingTime.practitioner);
                    }
                    return time;
                });
            otherServicesDuration += Number(currentServicesTimes[0].duration);
        }

        const minimisedTimes = minimalGapTimes(combinedTimes);

        const isFirst =
            times.length > 0 &&
            times[0] &&
            Array.isArray(times[0]) &&
            !times[0].some(time => time?.nextToService === true);

        const optimalTimes = [
            ...new Set([...minimisedTimes, ...additionalTimes(alsoShowFirst, alsoShowLast, combinedTimes)])
        ].sort((a, b) => new Date(a.value) - new Date(b.value));

        if ((!isFirst && showAllApptsFirst && minimiseGaps) || (minimiseGaps && !showAllApptsFirst)) {
            combinedTimes = optimalTimes;
        }

        return combinedTimes.map((el, index) => {
            const date = getCurrentTimezonedDate(el.value);
            return (
                <div
                    key={index}
                    className={`${classes.hourOption} ${selectedHour &&
                        date.getTime() === selectedHour.value.getTime() &&
                        classes.selectedOption} ${!el.valid && classes.disabledOption}`}
                    onClick={() => {
                        if (el.valid) {
                            handleSelectedHour({
                                ...el,
                                value: date
                            });
                        }
                    }}
                >
                    {`${formatDate(el.value)}`}
                </div>
            );
        });
    };

    const handleSelectedHour = hour => {
        dispatch(setCurrentConfirmStep(null));
        handleUpdateCurrAppts(hour);
        dispatch(changeStaff(getSelectedStaff(hour)));
        dispatch(chooseTime(hour.value));
    };

    useEffect(() => {
        if (!currentTime) return;

        setSelectedHour({ value: currentTime });
    }, [currentTime]);

    const handleUpdateCurrAppts = hour => {
        const newCurrentApptsState = currentAppt.map((appt, index) => {
            // const serviceId = appt.service?.id || appt.service?._id;
            // const isCourseService = courseContext?.courseServices && serviceId && courseContext.courseServices.includes(serviceId);

            const isRegularServiceBooking = appt.service?._isRegularServiceBooking;

            const timeInfo = hour
                ? {
                      time: hour.startDate,
                      endTime: hour.endDate
                  }
                : {};

            if (!appt.staff) {
                const selectedStaff = getSelectedStaff(hour, index);

                // Look up the correct price for this specific service, not the main service
                const locationItem = getLocationItem(appt.service, currentLocation.id);
                const staffPricing = locationItem?.staffs?.find(s => s.staff?.toString() === selectedStaff?.id);
                const correctedStaff = selectedStaff
                    ? {
                          ...selectedStaff,
                          grossPrice:
                              staffPricing?.grossPrice ??
                              selectedStaff?.grossPrice ??
                              locationItem?.grossPrice ??
                              appt.service?.defaultPrice ??
                              0,
                          netPrice:
                              staffPricing?.netPrice ??
                              selectedStaff?.netPrice ??
                              locationItem?.netPrice ??
                              appt.service?.defaultNetPrice ??
                              0
                      }
                    : selectedStaff;

                return {
                    ...appt,
                    ...timeInfo,
                    staff: correctedStaff,
                    service: {
                        ...appt.service,
                        _isRegularServiceBooking: isRegularServiceBooking
                    }
                };
            }

            return {
                ...appt,
                ...timeInfo,
                service: {
                    ...appt.service,
                    _isRegularServiceBooking: isRegularServiceBooking
                }
            };
        });

        dispatch(updateAppointments(newCurrentApptsState));
    };

    const handleBookAppointment = () => {
        if (user.id) {
            dispatch(changeStep(CONFIRM));

            const confirmUrl = `${url}/confirm${location.search}`;
            history.push(confirmUrl);
        } else {
            setIsLoginOpen(true);
            dispatch(changeModalVisibility({ visible: true, blockClose: true }));
        }
        if (!isReschedule) {
            if (countdown === 0) {
                setCountdown(300);
            }

            if (!selectedHour) {
                handleCountdown();
            }
        }
    };

    return (
        <div className={classes.padd1}>
            <div className={classes.timeChoose}>
                <div className={classes.timeChooseTitle}>
                    <h3 className={classes.text} style={isMobile ? { marginTop: 0, marginBottom: 0 } : {}}>
                        {currentDate.toDateString()}
                    </h3>
                </div>
                {isMobile && displayFade.top && <div className={classes.fadeTop} />}
                <div
                    ref={scrollerRef}
                    className={['overflowVisible', classes.maxHeight].join(' ')}
                    style={isMobile ? { marginTop: 0 } : {}}
                >
                    <div className={classes.contentOptions}>
                        {isLoading ? <CircularProgress /> : renderTimeOptions()}
                    </div>
                </div>
                {isMobile && displayFade.bottom && <div className={classes.fadeBottom} />}
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBookAppointment}
                        disabled={!selectedHour}
                        autoFocus
                    >
                        {isReschedule ? 'Reschedule appointment' : 'Book appointment'}
                    </Button>
                </div>
            </div>

            <Timeout time={countdown} />
        </div>
    );
};

export default withStyles(timeStyle)(TimeChoose);
